import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import {
  MapPin, Navigation, Flag, Play, Square, ChevronRight, ChevronLeft,
  Plus, Minus, Trophy, Clock, AlertTriangle, Battery, Check, Loader2,
  Target, Zap, X, Users, Share2, Download, Image
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const greenIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const playerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const flagIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
}

function getAuthHeaders() {
  const token = localStorage.getItem('session_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

function haversineYards(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const toRad = d => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const m = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(m * 1.09361);
}

// ==================== PHASES ====================

const PHASE = { SELECT_COURSE: 0, PLAYING: 1, SUMMARY: 2 };

export default function SmartRound() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [phase, setPhase] = useState(PHASE.SELECT_COURSE);
  const [gpsPosition, setGpsPosition] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [teeBox, setTeeBox] = useState('white');
  const [loading, setLoading] = useState(false);
  const [round, setRound] = useState(null);
  const [courseData, setCourseData] = useState(null);
  const [currentHole, setCurrentHole] = useState(1);
  const [distanceToGreen, setDistanceToGreen] = useState(null);
  const [scores, setScores] = useState({});
  const [putts, setPutts] = useState({});
  const [penalties, setPenalties] = useState({});
  const [leaderboard, setLeaderboard] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showScoreInput, setShowScoreInput] = useState(false);
  const [holeStartTime, setHoleStartTime] = useState(null);
  const [holeTimer, setHoleTimer] = useState(0);
  const [lowPower, setLowPower] = useState(false);
  const [summary, setSummary] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [activityFeed, setActivityFeed] = useState([]);
  const watchRef = useRef(null);
  const timerRef = useRef(null);
  const summaryRef = useRef(null);
  const wsRef = useRef(null);

  // ==================== GPS ====================

  useEffect(() => {
    if (!navigator.geolocation) {
      setGpsError('GPS not supported on this device');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => setGpsPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setGpsError('Please enable location services'),
      { enableHighAccuracy: true }
    );
  }, []);

  const startWatching = useCallback(() => {
    if (watchRef.current) navigator.geolocation.clearWatch(watchRef.current);
    watchRef.current = navigator.geolocation.watchPosition(
      pos => {
        const newPos = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setGpsPosition(newPos);
      },
      () => {},
      { enableHighAccuracy: !lowPower, maximumAge: lowPower ? 10000 : 3000, timeout: 15000 }
    );
  }, [lowPower]);

  useEffect(() => {
    return () => {
      if (watchRef.current) navigator.geolocation.clearWatch(watchRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  // WebSocket connection for real-time activity
  useEffect(() => {
    if (phase !== PHASE.PLAYING) return;
    const wsUrl = API.replace('https://', 'wss://').replace('http://', 'ws://') + '/smart-round/ws/activity';
    try {
      const ws = new WebSocket(wsUrl);
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          setActivityFeed(prev => [msg, ...prev].slice(0, 20));
        } catch {}
      };
      ws.onopen = () => {
        // Send ping every 30s to keep alive
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 30000);
        ws._pingInterval = pingInterval;
      };
      ws.onclose = () => { if (ws._pingInterval) clearInterval(ws._pingInterval); };
      wsRef.current = ws;
    } catch {}
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, [phase]);

  // Fetch active tournaments for linking
  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        const res = await fetch(`${API}/smart-round/active-tournaments`);
        if (res.ok) { const d = await res.json(); setTournaments(d.tournaments || []); }
      } catch {}
    };
    fetchTournaments();
  }, []);

  // ==================== HOLE TIMER ====================

  useEffect(() => {
    if (phase === PHASE.PLAYING) {
      setHoleStartTime(Date.now());
      timerRef.current = setInterval(() => {
        setHoleTimer(Math.floor((Date.now() - (holeStartTime || Date.now())) / 1000));
      }, 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [phase, currentHole]);

  // ==================== DISTANCE CALC ====================

  useEffect(() => {
    if (!gpsPosition || !courseData) return;
    const hole = courseData.holes?.find(h => h.number === currentHole);
    if (hole?.green?.lat && hole?.green?.lng) {
      setDistanceToGreen(haversineYards(gpsPosition.lat, gpsPosition.lng, hole.green.lat, hole.green.lng));
    }
  }, [gpsPosition, currentHole, courseData]);

  // ==================== GPS POSITION UPDATE TO SERVER ====================

  useEffect(() => {
    if (phase !== PHASE.PLAYING || !round || !gpsPosition) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API}/smart-round/update-position`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          credentials: 'include',
          body: JSON.stringify({ round_id: round.round_id, lat: gpsPosition.lat, lng: gpsPosition.lng })
        });
        if (res.ok) {
          const data = await res.json();
          if (data.current_hole !== currentHole) {
            setCurrentHole(data.current_hole);
            setHoleStartTime(Date.now());
          }
          if (data.distance_to_green) setDistanceToGreen(data.distance_to_green);
        }
      } catch {}
    }, lowPower ? 15000 : 8000);
    return () => clearInterval(interval);
  }, [phase, round, gpsPosition, currentHole, lowPower]);

  // ==================== LEADERBOARD POLLING ====================

  useEffect(() => {
    if (phase !== PHASE.PLAYING || !round) return;
    const fetchLB = async () => {
      try {
        const res = await fetch(`${API}/smart-round/leaderboard/${round.round_id}`, { credentials: 'include' });
        if (res.ok) setLeaderboard(await res.json());
      } catch {}
    };
    fetchLB();
    const interval = setInterval(fetchLB, 10000);
    return () => clearInterval(interval);
  }, [phase, round]);

  // ==================== COURSE SELECTION ====================

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const url = gpsPosition
        ? `${API}/smart-round/courses/nearby?lat=${gpsPosition.lat}&lng=${gpsPosition.lng}`
        : `${API}/smart-round/courses`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setCourses(data.courses || []);
      }
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchCourses(); }, [gpsPosition]);

  const startRound = async () => {
    if (!selectedCourse) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/smart-round/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ course_id: selectedCourse.course_id, tee_box: teeBox, tournament_id: selectedTournament?.tournament_id })
      });
      if (res.ok) {
        const data = await res.json();
        setRound(data);
        setCourseData(data.course);
        setCurrentHole(1);
        setPhase(PHASE.PLAYING);
        startWatching();
      }
    } catch {} finally { setLoading(false); }
  };

  // ==================== SCORING ====================

  const submitScore = async () => {
    const strokes = scores[currentHole];
    if (!strokes || strokes < 1) return;
    try {
      await fetch(`${API}/smart-round/score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          round_id: round.round_id,
          hole_number: currentHole,
          strokes,
          putts: putts[currentHole] || 0,
          penalties: penalties[currentHole] || 0,
        })
      });
      setShowScoreInput(false);
      // Auto advance
      const totalHoles = courseData?.holes?.length || 18;
      if (currentHole < totalHoles) {
        const next = currentHole + 1;
        setCurrentHole(next);
        setHoleStartTime(Date.now());
        await fetch(`${API}/smart-round/advance-hole`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          credentials: 'include',
          body: JSON.stringify({ round_id: round.round_id, next_hole: next })
        });
      }
    } catch {}
  };

  const endRound = async () => {
    try {
      const res = await fetch(`${API}/smart-round/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ round_id: round.round_id })
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
        setPhase(PHASE.SUMMARY);
        if (watchRef.current) navigator.geolocation.clearWatch(watchRef.current);
      }
    } catch {}
  };

  const setScoreVal = (hole, val) => setScores(p => ({ ...p, [hole]: Math.max(1, val) }));
  const setPuttVal = (hole, val) => setPutts(p => ({ ...p, [hole]: Math.max(0, val) }));
  const setPenVal = (hole, val) => setPenalties(p => ({ ...p, [hole]: Math.max(0, val) }));

  const formatTimer = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const holeData = courseData?.holes?.find(h => h.number === currentHole);

  const totalStrokes = Object.values(scores).reduce((a, b) => a + b, 0);
  const holesScored = Object.keys(scores).length;
  const parThrough = courseData?.holes?.filter(h => scores[h.number]).reduce((a, h) => a + h.par, 0) || 0;
  const toPar = holesScored > 0 ? totalStrokes - parThrough : 0;

  // Share to social feed
  const shareToFeed = async () => {
    if (!summary || sharing) return;
    setSharing(true);
    try {
      const myStats = summary.summaries?.[0];
      if (!myStats) return;
      const toParStr = myStats.to_par > 0 ? `+${myStats.to_par}` : myStats.to_par === 0 ? 'E' : myStats.to_par;
      const caption = `Shot ${myStats.total_strokes} (${toParStr}) at ${summary.course_name}. ${myStats.birdies} birdies, ${myStats.pars} pars across ${myStats.holes_played} holes.`;

      // Peer-verification: pull other Bundle members who played this SmartRound with the user
      const taggedPlayers = Array.from(new Set(
        (summary.summaries || [])
          .slice(1) // skip index 0 which is the poster themselves
          .map(s => s.user_id)
          .filter(id => id && id !== summary.summaries?.[0]?.user_id)
      ));

      const res = await fetch(`${API}/social/posts/round`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          course_name: summary.course_name,
          date: new Date().toISOString().split('T')[0],
          score: myStats.total_strokes,
          winnings: 0,
          caption,
          tagged_players: taggedPlayers,
        })
      });
      if (res.ok) {
        setShared(true);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err?.detail || 'Failed to share — make sure you played with at least one Bundle member.');
      }
    } catch {} finally { setSharing(false); }
  };

  // Export as image via canvas
  const exportAsImage = async () => {
    const el = summaryRef.current;
    if (!el) return;
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(el, { backgroundColor: '#0a1a0f', scale: 2 });
      const link = document.createElement('a');
      link.download = `round-${summary?.course_name?.replace(/\s+/g, '-') || 'summary'}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch {
      // Fallback: copy text
      const myStats = summary?.summaries?.[0];
      if (myStats) {
        const text = `${summary.course_name}\nScore: ${myStats.total_strokes} (${myStats.to_par > 0 ? '+' : ''}${myStats.to_par})\nBirdies: ${myStats.birdies} | Pars: ${myStats.pars} | Bogeys: ${myStats.bogeys}`;
        navigator.clipboard?.writeText(text);
        alert('Round summary copied to clipboard!');
      }
    }
  };

  // ==================== RENDER: COURSE SELECT ====================

  if (phase === PHASE.SELECT_COURSE) {
    return (
      <div className="min-h-screen bg-[#0a1a0f] text-white pb-mobile-nav pt-16">
        <div className="max-w-lg mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <Navigation className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl font-black" data-testid="smart-round-title">Start Round</h1>
              <p className="text-sm text-gray-400">GPS-powered live scoring</p>
            </div>
          </div>

          {/* GPS Status */}
          <div className={`flex items-center gap-2 px-4 py-3 rounded-xl mb-6 ${gpsPosition ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
            <MapPin className={`w-4 h-4 ${gpsPosition ? 'text-emerald-400' : 'text-red-400'}`} />
            <span className={`text-sm ${gpsPosition ? 'text-emerald-300' : 'text-red-300'}`}>
              {gpsPosition ? 'GPS Active — Location detected' : (gpsError || 'Acquiring GPS...')}
            </span>
          </div>

          {/* Course List */}
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold">
              {gpsPosition ? 'Nearby Courses' : 'All Courses'}
            </h2>
            {gpsPosition && (
              <button
                data-testid="show-all-courses-btn"
                onClick={() => {
                  fetch(`${API}/smart-round/courses`)
                    .then(r => r.json())
                    .then(d => setCourses(d.courses || []))
                    .catch(() => {});
                }}
                className="text-xs text-emerald-400 hover:underline"
              >
                Show All Courses
              </button>
            )}
          </div>

          {/* Search */}
          <input
            data-testid="course-search"
            type="text"
            placeholder="Search courses..."
            onChange={e => {
              const q = e.target.value.toLowerCase();
              if (!q) { fetchCourses(); return; }
              setCourses(prev => prev.filter(c =>
                c.name.toLowerCase().includes(q) || c.city?.toLowerCase().includes(q) || c.state?.toLowerCase().includes(q)
              ));
            }}
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-gray-600 focus:outline-none focus:border-emerald-500/50 mb-3"
          />

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
            </div>
          ) : courses.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No GPS-mapped courses found nearby</p>
          ) : (
            <div className="space-y-3" data-testid="course-list">
              {courses.map(c => (
                <button
                  key={c.course_id}
                  data-testid={`course-${c.course_id}`}
                  onClick={() => setSelectedCourse(c)}
                  className={`w-full text-left p-4 rounded-xl border transition-all ${
                    selectedCourse?.course_id === c.course_id
                      ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-white/10 bg-white/5 hover:border-white/20'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-bold">{c.name}</div>
                      <div className="text-sm text-gray-400">{c.city}, {c.state}</div>
                    </div>
                    {c.distance_miles && (
                      <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-full">
                        {c.distance_miles} mi
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Tee Box Selection */}
          {selectedCourse && (
            <div className="mt-6">
              <h3 className="text-sm text-gray-400 mb-3">Select Tee Box</h3>
              <div className="flex gap-2" data-testid="tee-box-selector">
                {(selectedCourse.tee_boxes || ['white', 'blue', 'gold', 'red']).map(t => (
                  <button
                    key={t}
                    data-testid={`tee-${t}`}
                    onClick={() => setTeeBox(t)}
                    className={`flex-1 py-3 rounded-xl border text-sm font-bold capitalize transition-all ${
                      teeBox === t
                        ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                        : 'border-white/10 bg-white/5 text-gray-400 hover:border-white/20'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Start Button */}
          {selectedCourse && (
            <>
              {/* Tournament Link (optional) */}
              {tournaments.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm text-gray-400 mb-3">Link to Tournament (optional)</h3>
                  <div className="space-y-2" data-testid="tournament-selector">
                    <button
                      onClick={() => setSelectedTournament(null)}
                      className={`w-full text-left p-3 rounded-xl border text-sm transition-all ${
                        !selectedTournament ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-white/10 bg-white/5 text-gray-400'
                      }`}
                    >
                      No tournament — casual round
                    </button>
                    {tournaments.map(t => (
                      <button
                        key={t.tournament_id}
                        data-testid={`tournament-${t.tournament_id}`}
                        onClick={() => setSelectedTournament(t)}
                        className={`w-full text-left p-3 rounded-xl border text-sm transition-all ${
                          selectedTournament?.tournament_id === t.tournament_id
                            ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                            : 'border-white/10 bg-white/5 text-gray-400 hover:border-white/20'
                        }`}
                      >
                        <div className="font-bold text-white">{t.name}</div>
                        <div className="text-xs text-gray-500">{t.status} {t.course_name ? `• ${t.course_name}` : ''}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <Button
                data-testid="start-round-btn"
                onClick={startRound}
                disabled={loading}
                className="w-full mt-6 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-lg py-6 rounded-xl"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Play className="w-5 h-5 mr-2" />}
                {selectedTournament ? `Start Tournament Round` : `Start Round at ${selectedCourse.name.split('(')[0].trim()}`}
              </Button>

              {selectedTournament && (
                <p className="text-center text-xs text-emerald-400/60 mt-2">
                  Scores will auto-sync to {selectedTournament.name}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  // ==================== RENDER: PLAYING ====================

  if (phase === PHASE.PLAYING) {
    const mapCenter = gpsPosition ? [gpsPosition.lat, gpsPosition.lng] : [38.87, -77.03];

    return (
      <div className="min-h-screen bg-[#0a1a0f] text-white pb-mobile-nav pt-16">
        {/* Top Bar */}
        <div className="fixed top-14 left-0 right-0 z-40 bg-[#0a1a0f]/95 backdrop-blur-md border-b border-white/10 px-4 py-2">
          <div className="max-w-lg mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="text-center">
                <div className="text-2xl font-black text-emerald-400" data-testid="current-hole-number">
                  {currentHole}
                </div>
                <div className="text-[10px] text-gray-500 uppercase">Hole</div>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div className="text-center">
                <div className="text-lg font-bold">{holeData?.par || 4}</div>
                <div className="text-[10px] text-gray-500 uppercase">Par</div>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div className="text-center">
                <div className="text-lg font-bold">{holeData?.yards || '--'}</div>
                <div className="text-[10px] text-gray-500 uppercase">Yards</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-right">
                <div className={`text-lg font-black ${toPar > 0 ? 'text-red-400' : toPar < 0 ? 'text-emerald-400' : 'text-white'}`}>
                  {toPar > 0 ? `+${toPar}` : toPar === 0 ? 'E' : toPar}
                </div>
                <div className="text-[10px] text-gray-500">{holesScored} holes</div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 pt-20">
          {/* Distance to Green */}
          <div className="text-center mb-4" data-testid="distance-display">
            <div className="text-6xl font-black text-emerald-400 tabular-nums">
              {distanceToGreen ?? '--'}
            </div>
            <div className="text-sm text-gray-400 uppercase tracking-wider">Yards to Green</div>
          </div>

          {/* Map */}
          <div className="rounded-2xl overflow-hidden border border-white/10 mb-4 h-[250px]" data-testid="gps-map">
            <MapContainer
              center={mapCenter}
              zoom={17}
              style={{ height: '100%', width: '100%' }}
              zoomControl={false}
            >
              <TileLayer
                attribution='&copy; Esri'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
              <MapUpdater center={mapCenter} />

              {/* Player position */}
              {gpsPosition && (
                <Marker position={[gpsPosition.lat, gpsPosition.lng]} icon={playerIcon}>
                  <Popup>You are here</Popup>
                </Marker>
              )}

              {/* Green marker */}
              {holeData?.green?.lat && (
                <Marker position={[holeData.green.lat, holeData.green.lng]} icon={greenIcon}>
                  <Popup>Hole {currentHole} Green</Popup>
                </Marker>
              )}

              {/* Tee marker */}
              {holeData?.tee?.lat && (
                <Marker position={[holeData.tee.lat, holeData.tee.lng]} icon={flagIcon}>
                  <Popup>Hole {currentHole} Tee</Popup>
                </Marker>
              )}

              {/* Green circle */}
              {holeData?.green?.lat && (
                <Circle
                  center={[holeData.green.lat, holeData.green.lng]}
                  radius={20}
                  pathOptions={{ color: '#10b981', fillColor: '#10b981', fillOpacity: 0.3 }}
                />
              )}
            </MapContainer>
          </div>

          {/* Hole Timer + Controls */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-gray-400">
              <Clock className="w-4 h-4" />
              <span className="text-sm tabular-nums">{formatTimer(holeTimer)}</span>
              {holeTimer > 900 && <AlertTriangle className="w-4 h-4 text-yellow-400" />}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLowPower(!lowPower)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs ${lowPower ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/5 text-gray-500'}`}
              >
                <Battery className="w-3 h-3" />
                {lowPower ? 'Low Power' : 'Normal'}
              </button>
              <button
                data-testid="show-leaderboard-btn"
                onClick={() => setShowLeaderboard(true)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 text-gray-400 text-xs"
              >
                <Trophy className="w-3 h-3" /> Board
              </button>
            </div>
          </div>

          {/* Score Entry Button */}
          <Button
            data-testid="enter-score-btn"
            onClick={() => {
              if (!scores[currentHole]) setScores(p => ({ ...p, [currentHole]: holeData?.par || 4 }));
              setShowScoreInput(true);
            }}
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-lg py-5 rounded-xl mb-3"
          >
            <Target className="w-5 h-5 mr-2" />
            Enter Score — Hole {currentHole}
          </Button>

          {/* Manual Hole Navigation */}
          <div className="flex gap-2 mb-4">
            <Button
              onClick={() => { if (currentHole > 1) setCurrentHole(currentHole - 1); }}
              disabled={currentHole <= 1}
              variant="outline"
              className="flex-1 border-white/10 text-gray-400 hover:bg-white/5"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Prev Hole
            </Button>
            <Button
              onClick={() => { if (currentHole < (courseData?.holes?.length || 18)) setCurrentHole(currentHole + 1); }}
              disabled={currentHole >= (courseData?.holes?.length || 18)}
              variant="outline"
              className="flex-1 border-white/10 text-gray-400 hover:bg-white/5"
            >
              Next Hole <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          {/* Mini Scorecard */}
          <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-4">
            <h3 className="text-sm font-bold text-gray-400 mb-3 uppercase tracking-wider">Scorecard</h3>
            <div className="grid grid-cols-9 gap-1 text-center text-xs">
              {courseData?.holes?.slice(0, 9).map(h => (
                <div key={h.number}>
                  <div className="text-gray-600">{h.number}</div>
                  <div className="text-gray-500 text-[10px]">{h.par}</div>
                  <div className={`font-bold rounded py-0.5 ${
                    scores[h.number]
                      ? scores[h.number] < h.par ? 'text-emerald-400 bg-emerald-500/10'
                        : scores[h.number] > h.par ? 'text-red-400 bg-red-500/10'
                        : 'text-white bg-white/10'
                      : 'text-gray-600'
                  }`}>
                    {scores[h.number] || '-'}
                  </div>
                </div>
              ))}
            </div>
            {(courseData?.holes?.length || 0) > 9 && (
              <div className="grid grid-cols-9 gap-1 text-center text-xs mt-2 pt-2 border-t border-white/5">
                {courseData?.holes?.slice(9, 18).map(h => (
                  <div key={h.number}>
                    <div className="text-gray-600">{h.number}</div>
                    <div className="text-gray-500 text-[10px]">{h.par}</div>
                    <div className={`font-bold rounded py-0.5 ${
                      scores[h.number]
                        ? scores[h.number] < h.par ? 'text-emerald-400 bg-emerald-500/10'
                          : scores[h.number] > h.par ? 'text-red-400 bg-red-500/10'
                          : 'text-white bg-white/10'
                        : 'text-gray-600'
                    }`}>
                      {scores[h.number] || '-'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Live Activity Feed */}
          {activityFeed.length > 0 && (
            <div className="bg-white/5 rounded-xl p-4 border border-white/10 mb-4" data-testid="live-activity-feed">
              <h3 className="text-sm font-bold text-gray-400 mb-2 uppercase tracking-wider flex items-center gap-2">
                <Zap className="w-3 h-3 text-emerald-400" /> Live Activity
              </h3>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {activityFeed.slice(0, 5).map((a, i) => (
                  <div key={i} className="text-xs text-gray-400 flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />
                    <span>
                      {a.type === 'score_update' && <><span className="text-white font-medium">{a.user_name}</span> scored {a.strokes} on hole {a.hole}</>}
                      {a.type === 'round_started' && <><span className="text-white font-medium">{a.user_name}</span> started a round at {a.course_name}</>}
                      {a.type === 'general' && a.message}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tournament Sync Indicator */}
          {round?.tournament_id && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 mb-4" data-testid="tournament-sync-indicator">
              <Trophy className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-emerald-300">Syncing scores to <span className="font-bold">{round.tournament_name || 'Tournament'}</span></span>
            </div>
          )}

          {/* End Round */}
          <Button
            data-testid="end-round-btn"
            onClick={endRound}
            variant="outline"
            className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 mb-6"
          >
            <Square className="w-4 h-4 mr-2" /> End Round
          </Button>
        </div>

        {/* Score Input Modal */}
        {showScoreInput && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowScoreInput(false)}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <div className="relative bg-[#111916] rounded-t-3xl sm:rounded-2xl w-full max-w-md p-6 border-t border-white/10" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-black">Hole {currentHole} — Par {holeData?.par || 4}</h3>
                <button onClick={() => setShowScoreInput(false)} className="p-2 rounded-lg hover:bg-white/10">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Strokes */}
              <div className="mb-5">
                <label className="text-sm text-gray-400 mb-2 block">Strokes</label>
                <div className="flex items-center justify-center gap-6">
                  <button data-testid="score-minus" onClick={() => setScoreVal(currentHole, (scores[currentHole] || holeData?.par || 4) - 1)} className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/20">
                    <Minus className="w-5 h-5" />
                  </button>
                  <span className="text-5xl font-black tabular-nums w-20 text-center" data-testid="score-value">
                    {scores[currentHole] || holeData?.par || 4}
                  </span>
                  <button data-testid="score-plus" onClick={() => setScoreVal(currentHole, (scores[currentHole] || holeData?.par || 4) + 1)} className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/20">
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Putts */}
              <div className="flex gap-4 mb-5">
                <div className="flex-1">
                  <label className="text-sm text-gray-400 mb-2 block">Putts</label>
                  <div className="flex items-center justify-center gap-4">
                    <button onClick={() => setPuttVal(currentHole, (putts[currentHole] || 0) - 1)} className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="text-2xl font-bold tabular-nums w-8 text-center">{putts[currentHole] || 0}</span>
                    <button onClick={() => setPuttVal(currentHole, (putts[currentHole] || 0) + 1)} className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="flex-1">
                  <label className="text-sm text-gray-400 mb-2 block">Penalties</label>
                  <div className="flex items-center justify-center gap-4">
                    <button onClick={() => setPenVal(currentHole, (penalties[currentHole] || 0) - 1)} className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="text-2xl font-bold tabular-nums w-8 text-center">{penalties[currentHole] || 0}</span>
                    <button onClick={() => setPenVal(currentHole, (penalties[currentHole] || 0) + 1)} className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              <Button
                data-testid="submit-score-btn"
                onClick={submitScore}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-lg py-5 rounded-xl"
              >
                <Check className="w-5 h-5 mr-2" />
                Save & Next Hole
              </Button>
            </div>
          </div>
        )}

        {/* Leaderboard Modal */}
        {showLeaderboard && leaderboard && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowLeaderboard(false)}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <div className="relative bg-[#111916] rounded-t-3xl sm:rounded-2xl w-full max-w-md p-6 border-t border-white/10 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-black flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-emerald-400" /> Live Leaderboard
                </h3>
                <button onClick={() => setShowLeaderboard(false)} className="p-2 rounded-lg hover:bg-white/10">
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <div className="space-y-2">
                {leaderboard.leaderboard?.map((p, i) => (
                  <div key={p.user_id} className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-center gap-3">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                        i === 0 ? 'bg-yellow-400 text-yellow-900' : 'bg-white/10 text-gray-400'
                      }`}>{i + 1}</span>
                      <div>
                        <div className="font-bold text-sm">{p.name}</div>
                        <div className="text-xs text-gray-500">Hole {p.current_hole} • {p.holes_played} scored</div>
                      </div>
                    </div>
                    <div className={`text-lg font-black ${
                      p.to_par > 0 ? 'text-red-400' : p.to_par < 0 ? 'text-emerald-400' : 'text-white'
                    }`}>
                      {p.to_par > 0 ? `+${p.to_par}` : p.to_par === 0 ? 'E' : p.to_par}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==================== RENDER: SUMMARY ====================

  if (phase === PHASE.SUMMARY && summary) {
    return (
      <div className="min-h-screen bg-[#0a1a0f] text-white pb-mobile-nav pt-16">
        <div className="max-w-lg mx-auto px-4 py-6">
          <div ref={summaryRef} className="pb-4">
            <div className="text-center mb-8">
              <Trophy className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
              <h1 className="text-3xl font-black" data-testid="round-complete-title">Round Complete</h1>
              <p className="text-gray-400">{summary.course_name}</p>
            </div>

            <div className="space-y-3">
              {summary.summaries?.map((p, i) => (
                <div key={p.user_id} className="p-5 rounded-2xl bg-white/5 border border-white/10">
                  <div className="flex justify-between items-center mb-3">
                    <div className="font-bold text-lg">{p.name}</div>
                    <div className={`text-2xl font-black ${
                      p.to_par > 0 ? 'text-red-400' : p.to_par < 0 ? 'text-emerald-400' : 'text-white'
                    }`}>
                      {p.total_strokes} ({p.to_par > 0 ? `+${p.to_par}` : p.to_par === 0 ? 'E' : p.to_par})
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3 text-center">
                    <div className="bg-white/5 rounded-lg py-2">
                      <div className="text-lg font-bold">{p.holes_played}</div>
                      <div className="text-[10px] text-gray-500">Holes</div>
                    </div>
                    <div className="bg-emerald-500/10 rounded-lg py-2">
                      <div className="text-lg font-bold text-emerald-400">{p.birdies}</div>
                      <div className="text-[10px] text-gray-500">Birdies</div>
                    </div>
                    <div className="bg-white/5 rounded-lg py-2">
                      <div className="text-lg font-bold">{p.pars}</div>
                      <div className="text-[10px] text-gray-500">Pars</div>
                    </div>
                    <div className="bg-red-500/10 rounded-lg py-2">
                      <div className="text-lg font-bold text-red-400">{p.bogeys}</div>
                      <div className="text-[10px] text-gray-500">Bogeys</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Share Actions */}
          <div className="flex gap-3 mt-6 mb-4">
            <Button
              data-testid="share-to-feed-btn"
              onClick={shareToFeed}
              disabled={sharing || shared}
              className={`flex-1 font-bold py-4 rounded-xl ${shared ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-emerald-500 hover:bg-emerald-400 text-black'}`}
            >
              {shared ? <><Check className="w-5 h-5 mr-2" />Shared to Feed</> : sharing ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Sharing...</> : <><Share2 className="w-5 h-5 mr-2" />Share to Feed</>}
            </Button>
            <Button
              data-testid="export-image-btn"
              onClick={exportAsImage}
              variant="outline"
              className="border-white/20 text-gray-300 hover:bg-white/5 py-4 rounded-xl"
            >
              <Download className="w-5 h-5" />
            </Button>
          </div>

          <Button
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="w-full border-white/10 text-gray-400 hover:bg-white/5 font-bold text-lg py-5 rounded-xl"
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return null;
}
